import React, { useMemo } from "react";
import PropTypes from "prop-types";
import { useEditorState } from "@tiptap/react";
import {
  Baseline,
  Bold,
  Highlighter,
  IndentDecrease,
  IndentIncrease,
  Italic,
  List,
  Minus,
  RemoveFormatting,
  Strikethrough,
  Underline
} from "lucide-react";
import { tiptapEditor } from "../../locales/en";
import {
  EDITOR_DEFAULT_HIGHLIGHT_COLOR,
  EDITOR_DEFAULT_TEXT_COLOR,
  EDITOR_FONT_SIZES,
  EDITOR_HEX_COLOR_PATTERN
} from "../../utils/constants/appConstants";

/**
 * `<input type="color">` only accepts 7-character hex, while a stored note may
 * carry any CSS colour the previous editor wrote (`rgb(...)`, a named colour).
 * Anything it cannot show falls back to the default swatch.
 */
const toSwatchValue = (color, fallback) => {
  if (!color) return fallback;
  if (EDITOR_HEX_COLOR_PATTERN.test(color)) return color.toLowerCase();

  const rgb = color.match(/\d+/g);
  if (!rgb || rgb.length < 3) return fallback;

  const hex = rgb
    .slice(0, 3)
    .map((part) => Number(part).toString(16).padStart(2, "0"))
    .join("");
  return `#${hex}`;
};

const TiptapEditorToolbar = ({ editor }) => {
  // The editor does not re-render React on every transaction, so the toolbar
  // subscribes to just the state it paints. The selector's result is compared
  // deeply, so typing inside one mark does not re-render the buttons.
  const state = useEditorState({
    editor,
    selector: ({ editor: instance }) => {
      const textStyle = instance.getAttributes("textStyle");
      return {
        bold: instance.isActive("bold"),
        italic: instance.isActive("italic"),
        underline: instance.isActive("underline"),
        strike: instance.isActive("strike"),
        bulletList: instance.isActive("bulletList"),
        fontSize: textStyle.fontSize ?? "",
        color: toSwatchValue(textStyle.color, EDITOR_DEFAULT_TEXT_COLOR),
        backgroundColor: toSwatchValue(
          textStyle.backgroundColor,
          EDITOR_DEFAULT_HIGHLIGHT_COLOR
        )
      };
    }
  });

  const buttons = useMemo(
    () => [
      {
        name: "bulletList",
        toggle: true,
        label: tiptapEditor.bulletList,
        Icon: List,
        active: state.bulletList,
        run: (chain) => chain.toggleBulletList()
      },
      {
        name: "bold",
        toggle: true,
        label: tiptapEditor.bold,
        Icon: Bold,
        active: state.bold,
        run: (chain) => chain.toggleBold()
      },
      {
        name: "italic",
        toggle: true,
        label: tiptapEditor.italic,
        Icon: Italic,
        active: state.italic,
        run: (chain) => chain.toggleItalic()
      },
      {
        name: "underline",
        toggle: true,
        label: tiptapEditor.underline,
        Icon: Underline,
        active: state.underline,
        run: (chain) => chain.toggleUnderline()
      },
      {
        name: "strike",
        toggle: true,
        label: tiptapEditor.strike,
        Icon: Strikethrough,
        active: state.strike,
        run: (chain) => chain.toggleStrike()
      },
      {
        name: "outdent",
        label: tiptapEditor.outdent,
        Icon: IndentDecrease,
        active: false,
        run: (chain) => chain.outdent()
      },
      {
        name: "indent",
        label: tiptapEditor.indent,
        Icon: IndentIncrease,
        active: false,
        run: (chain) => chain.indent()
      },
      {
        name: "horizontalRule",
        label: tiptapEditor.horizontalRule,
        Icon: Minus,
        active: false,
        run: (chain) => chain.setHorizontalRule()
      },
      {
        name: "removeFormat",
        label: tiptapEditor.removeFormat,
        Icon: RemoveFormatting,
        active: false,
        run: (chain) => chain.unsetAllMarks()
      }
    ],
    [state]
  );

  const runOn = (run) => run(editor.chain().focus()).run();

  const handleFontSize = (event) => {
    const { value } = event.target;
    const chain = editor.chain().focus();
    if (value) chain.setFontSize(value).run();
    else chain.unsetFontSize().run();
  };

  return (
    <div
      className="tiptap-toolbar"
      role="toolbar"
      aria-label={tiptapEditor.toolbarLabel}
    >
      <select
        className="tiptap-toolbar__select"
        aria-label={tiptapEditor.fontSize}
        title={tiptapEditor.fontSize}
        value={state.fontSize}
        onChange={handleFontSize}
      >
        <option value="">{tiptapEditor.fontSizeDefault}</option>
        {EDITOR_FONT_SIZES.map((size) => (
          <option key={size} value={size}>
            {size}
          </option>
        ))}
      </select>

      {buttons.map(({ name, label, Icon, active, run, toggle }) => (
        <button
          key={name}
          type="button"
          className="tiptap-toolbar__button"
          title={label}
          aria-label={label}
          aria-pressed={toggle ? active : undefined}
          data-active={active}
          onClick={() => runOn(run)}
        >
          <Icon size={16} aria-hidden="true" />
        </button>
      ))}

      <label className="tiptap-toolbar__swatch" title={tiptapEditor.textColor}>
        <Baseline size={16} aria-hidden="true" />
        <input
          type="color"
          aria-label={tiptapEditor.textColor}
          value={state.color}
          onChange={(event) =>
            editor.chain().focus().setColor(event.target.value).run()
          }
        />
        <span
          className="tiptap-toolbar__swatch-bar"
          style={{ "--swatch-color": state.color }}
        />
      </label>

      <label
        className="tiptap-toolbar__swatch"
        title={tiptapEditor.highlightColor}
      >
        <Highlighter size={16} aria-hidden="true" />
        <input
          type="color"
          aria-label={tiptapEditor.highlightColor}
          value={state.backgroundColor}
          onChange={(event) =>
            editor.chain().focus().setBackgroundColor(event.target.value).run()
          }
        />
        <span
          className="tiptap-toolbar__swatch-bar"
          style={{ "--swatch-color": state.backgroundColor }}
        />
      </label>
    </div>
  );
};

TiptapEditorToolbar.propTypes = {
  editor: PropTypes.object.isRequired
};

export default TiptapEditorToolbar;
